import { makeAutoObservable } from 'mobx'
import { PropsWithChildren, createContext, useContext, useMemo } from 'react'
import { DetailForm } from '../../api/DTOtemp'

export class FormPackageControlApi {
	private _hiddenForms: string[]
	private _totalFormCount: number
	private _currentFormIndex = 0
	private readonly _allForms: DetailForm[]

	get hiddenForms(): ReadonlyArray<string> {
		return this._hiddenForms
	}

	get currentFormIndex(): number {
		return this._currentFormIndex
	}

	set currentFormIndex(index: number) {
		this._currentFormIndex = index
	}

	private get allFormNames() {
		return this._allForms.map(v => v.name.toUpperCase().trim())
	}

	public readonly apiId: number

	constructor(forms: DetailForm[]) {
		this.apiId = performance.now()
		console.log('created apid ', this.apiId)

		makeAutoObservable(this)

		this._totalFormCount = forms.length

		if (forms.length <= 1) this._hiddenForms = []
		else {
			const hiddenForms = forms.filter(
				(v) => v.formConfiguration.hiddenOnDefault,
			)
			// if all forms are hidden, show all of them by default
			if (hiddenForms.length === forms.length) this._hiddenForms = []
			else {
				this._hiddenForms = hiddenForms.map((v) => v.name.toUpperCase().trim())
			}
		}
		this._allForms = [...forms].sort((a, b) => a.ordinalPosition - b.ordinalPosition)
		let index = 0
		while (index < this._allForms.length) {
			if (!this.hiddenForms.includes(this.allFormNames[index])) {
				this._currentFormIndex = index
				return
			}
			index++
		}
		console.error("No available forms to display")
	}

	hideForm(formName: string) {
		// we don't want to hide a form if it's the only one in the package or if it's already been hidden
		if (
			!this.canHide() ||
			this._hiddenForms
				.map((v) => v.toUpperCase().trim())
				.includes(formName.toUpperCase().trim())
		)
			return
		this._hiddenForms.push(formName.toUpperCase().trim())
	}

	showForm(formName: string) {
		const indexToRemove = this._hiddenForms.indexOf(
			formName.toUpperCase().trim(),
		)
		if (indexToRemove > -1) this._hiddenForms.splice(indexToRemove, 1)
	}

	formIsHidden(formName: string): boolean {
		return this._hiddenForms
			.map((v) => v.toUpperCase().trim())
			.includes(formName.toUpperCase().trim())
	}

	canHide(): boolean {
		// we always need at least one form showing
		return this._hiddenForms.length < this._totalFormCount - 1
	}

	goToForm(formName: string) {
		const name = formName.toUpperCase().trim()
		if (!this.allFormNames
			.filter(v => !this._hiddenForms
				.map(v => v.toUpperCase().trim())
				.includes(v)
			).includes(name)
		) {
			console.error("form '" + formName + "' is either hidden or not found")
			return
		}
		this._currentFormIndex = this.allFormNames.findIndex(v => v === name)
	}

	goToNextForm() {
		let index = this._currentFormIndex + 1
		while (index < this._allForms.length) {
			if (!this.hiddenForms.includes(this.allFormNames[index])) {
				this._currentFormIndex = index
				return
			}
			index++
		}
		console.error("Unable to go to next form, current form is the last available in the package")
	}

	goToPreviousForm() {
		let index = this._currentFormIndex - 1
		while (index >= 0) {
			if (!this.hiddenForms.includes(this.allFormNames[index])) {
				this._currentFormIndex = index
				return
			}
			index--
		}
		console.error("Unable to go to previous form, current form is the first available in the package")
	}
}

const formPackageControlApiContext = createContext<FormPackageControlApi | undefined>(
	undefined,
)

type FormPackageControlApiContextProps = {
	forms: DetailForm[]
}

export const FormPackageControlApiContext = (
	props: PropsWithChildren<FormPackageControlApiContextProps>,
) => {
	const api = useMemo(() => {
		return new FormPackageControlApi(props.forms)
	}, [...props.forms])

	return (
		<formPackageControlApiContext.Provider value={api}>
			{props.children}
		</formPackageControlApiContext.Provider>
	)
}

export function useFormPackageControlApi(): FormPackageControlApi {
	const api = useContext(formPackageControlApiContext)
	if (api === undefined) throw new Error('form package control api was not defined')

	return api
}
